import React, { useState } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Container,
  Typography,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight
} from '@mui/icons-material';
import Page from 'src/components/Page';
import Iconify from 'src/components/Iconify';
import ViewServiceModal from 'src/sections/Companies/Viewservicemodal';
import EditServiceModal from 'src/sections/Companies/Editservicemodal';
import ChangeStatusModal from 'src/sections/Companies/Changestatusmodal';

const TABLE_DATA = Array.from({ length: 20 }, (_, index) => ({
  id: index + 1,
  company_name: index % 2 === 0 ? `Micromata GmbH ${index + 1}` : `Alvaro Pedro ${index + 1}`,
  email_address: index % 2 === 0 ? `info${index}@micromata.de` : `alvaro${index}@airbus.com`,
  joined_date: '2024-01-15',
  status: index % 3 === 0 ? 'active' : index % 3 === 1 ? 'pending' : 'deleted',
  account_type: index % 2 === 0 ? 'Company' : 'Employee'
}));

export default function Companies() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRow, setSelectedRow] = useState(null);
  const [page, setPage] = useState(1);
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);

  const handleAction = (type, row) => {
    setSelectedRow(row);
    if (type === 'view') setViewOpen(true);
    if (type === 'edit') setEditOpen(true);
    if (type === 'status') setStatusOpen(true);
  };

  const tableHeaders = ['Company Name', 'Email Address', 'Joined', 'Status', 'Actions'];

  return (
    <Page title="Company">
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}
          >
            <Typography variant="h4">Companies</Typography>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                placeholder="Search by company, email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#9CA3AF' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <ClearIcon />
                    </IconButton>
                  )
                }}
                sx={{ bgcolor: '#161C24', borderRadius: 1, '& fieldset': { borderColor: '#333' } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth sx={{ bgcolor: '#161C24' }}>
                <InputLabel sx={{ color: '#9CA3AF' }}>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ color: '#fff', '& fieldset': { borderColor: '#333' } }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="deleted">Deleted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="contained" sx={{ height: 56, bgcolor: '#3F90DF' }}>
                Search
              </Button>
            </Grid>
          </Grid>

          <Card sx={{ bgcolor: '#212B36', border: '1px solid #333' }}>
            <TableContainer>
              <Table sx={{ minWidth: 1000 }}>
                <TableHead sx={{ bgcolor: 'rgba(145, 158, 171, 0.12)' }}>
                  <TableRow>
                    {tableHeaders.map((head) => (
                      <TableCell
                        key={head}
                        sx={{ color: '#9CA3AF', borderBottom: '1px solid #333' }}
                      >
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {TABLE_DATA.slice((page - 1) * 5, page * 5).map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}
                    >
                      <TableCell sx={{ color: '#fff' }}>{row.company_name}</TableCell>
                      <TableCell sx={{ color: '#9CA3AF' }}>{row.email_address}</TableCell>
                      <TableCell sx={{ color: '#9CA3AF' }}>{row.joined_date}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.status}
                          sx={{
                            textTransform: 'capitalize',
                            bgcolor:
                              row.status === 'active'
                                ? 'rgba(84, 214, 44, 0.16)'
                                : row.status === 'pending'
                                ? 'rgba(255, 171, 0, 0.16)'
                                : 'rgba(255, 72, 66, 0.16)',
                            color:
                              row.status === 'active'
                                ? '#54D62C'
                                : row.status === 'pending'
                                ? '#FFAB00'
                                : '#FF4842'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex' }}>
                          <Tooltip title="View Service">
                            <IconButton
                              onClick={() => handleAction('view', row)}
                              sx={{ color: '#3F90DF' }}
                            >
                              <Iconify icon="carbon:view" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Service">
                            <IconButton
                              onClick={() => handleAction('edit', row)}
                              sx={{ color: '#3F90DF' }}
                            >
                              <Iconify icon="fluent:edit-20-regular" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Change Status">
                            <IconButton
                              onClick={() => handleAction('status', row)}
                              sx={{ color: '#3F90DF' }}
                            >
                              <Iconify icon="ic:baseline-sync-alt" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  sx={{ border: '1px solid #333', borderRadius: 1, color: '#9CA3AF' }}
                >
                  <KeyboardArrowLeft fontSize="small" />
                </IconButton>

                {[1, 2, 3, 4, 5].map((num) => (
                  <Button
                    key={num}
                    onClick={() => setPage(num)}
                    sx={{
                      minWidth: 36,
                      height: 36,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: page === num ? '#3F90DF' : '#333',
                      bgcolor: page === num ? 'rgba(0, 110, 230, 0.1)' : 'transparent',
                      color: page === num ? '#3F90DF' : '#fff'
                    }}
                  >
                    {num}
                  </Button>
                ))}

                <Typography sx={{ color: '#9CA3AF', px: 1 }}>...</Typography>

                <Button
                  onClick={() => setPage(10)}
                  sx={{
                    minWidth: 36,
                    height: 36,
                    borderRadius: 1,
                    border: '1px solid #333',
                    color: page === 10 ? '#3F90DF' : '#fff'
                  }}
                >
                  10
                </Button>

                <IconButton
                  disabled={page === 10}
                  onClick={() => setPage(page + 1)}
                  sx={{ border: '1px solid #333', borderRadius: 1, color: '#9CA3AF' }}
                >
                  <KeyboardArrowRight fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          </Card>
        </Box>
      </Container>

      <ViewServiceModal open={viewOpen} onClose={() => setViewOpen(false)} data={selectedRow} />
      <EditServiceModal open={editOpen} onClose={() => setEditOpen(false)} data={selectedRow} />
      <ChangeStatusModal
        open={statusOpen}
        onClose={() => setStatusOpen(false)}
        data={selectedRow}
      />
    </Page>
  );
}
